import React, { useEffect, useState } from 'react'
import { Gallery } from 'devextreme-react/gallery'
import { Button } from 'devextreme-react/button'

const ZOOM_MIN = 0.5
const ZOOM_MAX = 4
const ZOOM_STEP = 0.25

// Drag a zoomed photo with the mouse to move it around (the pane scrolls).
const startPan = (e: React.PointerEvent<HTMLDivElement>) => {
	const pane = e.currentTarget
	if (pane.closest<HTMLElement>('.clickable-popup-viewer')?.dataset.zoomed !== 'true') return
	const start = { x: e.clientX, y: e.clientY, left: pane.scrollLeft, top: pane.scrollTop }
	const move = (m: PointerEvent) => {
		pane.scrollLeft = start.left - (m.clientX - start.x)
		pane.scrollTop = start.top - (m.clientY - start.y)
	}
	const stop = () => {
		pane.classList.remove('clickable-popup-viewer-panning')
		pane.removeEventListener('pointermove', move)
		pane.removeEventListener('pointerup', stop)
		pane.removeEventListener('pointercancel', stop)
	}
	pane.setPointerCapture(e.pointerId)
	pane.classList.add('clickable-popup-viewer-panning')
	pane.addEventListener('pointermove', move)
	pane.addEventListener('pointerup', stop)
	pane.addEventListener('pointercancel', stop)
}

// Right-hand panel. Opens when the user clicks an <a data-photo> in the HTML
// the backend sent (see popups/partials/photos.blade.php).
export function PhotoViewer(props: {
	photos: string[]
	index: number
	onIndexChange: (index: number) => void
	onClose: () => void
}): JSX.Element {
	const { photos, index, onIndexChange, onClose } = props
	const [zoom, setZoom] = useState(1)
	// quarter turns per photo, kept while browsing the gallery
	const [turns, setTurns] = useState<Record<string, number>>({})

	// each photo opens at its original fit
	useEffect(() => setZoom(1), [index, photos])
	const rotateBy = (dir: 1 | -1) =>
		setTurns(t => ({ ...t, [photos[index]]: ((((t[photos[index]] ?? 0) + dir) % 4) + 4) % 4 }))
	const zoomBy = (delta: number) => setZoom(z => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z + delta)))

	return (
		<div className="clickable-popup-viewer" data-zoomed={zoom > 1} style={{ ['--viewer-zoom' as any]: zoom }}>
			<div className="clickable-popup-viewer-head">
				<span>{photos.length > 1 ? `Photo ${index + 1} of ${photos.length}` : 'Photo'}</span>
				<span>
					<Button
						icon="minus"
						stylingMode="text"
						hint="Zoom out"
						disabled={zoom <= ZOOM_MIN}
						onClick={() => zoomBy(-ZOOM_STEP)}
					/>
					<span className="clickable-popup-viewer-zoom">{Math.round(zoom * 100)}%</span>
					<Button
						icon="plus"
						stylingMode="text"
						hint="Zoom in"
						disabled={zoom >= ZOOM_MAX}
						onClick={() => zoomBy(ZOOM_STEP)}
					/>
					<Button
						icon="fullscreen"
						stylingMode="text"
						hint="Reset zoom"
						disabled={zoom === 1}
						onClick={() => setZoom(1)}
					/>
					<Button icon="undo" stylingMode="text" hint="Rotate left" onClick={() => rotateBy(-1)} />
					<Button icon="redo" stylingMode="text" hint="Rotate right" onClick={() => rotateBy(1)} />
					<Button
						icon="export"
						stylingMode="text"
						hint="Open original in a new tab"
						onClick={() => window.open(photos[index], '_blank', 'noopener')}
					/>
					<Button icon="close" stylingMode="text" hint="Close photo" onClick={onClose} />
				</span>
			</div>
			<Gallery
				key={photos.join('|')}
				dataSource={photos}
				selectedIndex={index}
				height="100%"
				width="100%"
				loop={false}
				swipeEnabled={zoom === 1}
				showNavButtons={photos.length > 1}
				showIndicator={photos.length > 1}
				onSelectionChanged={(e: any) => onIndexChange(e.component.option('selectedIndex'))}
				itemRender={(src: string) => (
					<div className="clickable-popup-viewer-pane" onPointerDown={startPan}>
						<div className="clickable-popup-viewer-box">
							<img
								src={src}
								alt=""
								draggable={false}
								className="clickable-popup-viewer-img"
								data-sideways={(turns[src] ?? 0) % 2 === 1}
								style={{ transform: `translate(-50%, -50%) rotate(${(turns[src] ?? 0) * 90}deg)` }}
							/>
						</div>
					</div>
				)}
			/>
		</div>
	)
}
